import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { ReportsType } from "pages/Books/Reports/Reports";
import { useMemo } from "react";
import {
  useByViewInteractiveReportQuery,
  useGetInteractiveReportQuery,
} from "store/apis/reports";
import { ReportByView } from "types/Models/report";
import { ReportFilterType } from "../ReportFilter";
import { formatViewReportData } from "../utils/formatViewReportData";

export const useViewByReportDataFormat = ({
  reportType,
  reportId = "",
  filter,
  useCachedReport,
  isVarianceReport,
  regenerateCount,
}: {
  reportType: ReportsType;
  reportId?: string;
  filter: ReportFilterType;
  useCachedReport?: boolean;
  isVarianceReport?: boolean;
  regenerateCount?: number;
}) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();

  const { data, ...apiStates } = useByViewInteractiveReportQuery(
    {
      groupId,
      entityId,
      reportType,
      accountingMethod: filter.ACCOUNTING_METHOD,
      startDate: filter.START_DATE || null,
      endDate: filter.END_DATE || null,
      showNonZeroAccounts: filter.HIDE_ZERO,
      comparison_report_type:
        filter.VIEW_BY ||
        filter.OTHER ||
        filter.COMPARE_BY ||
        (filter.TXN_GROUP_ID ? "CLASSIFICATION" : null),
      transaction_group_id: filter.TXN_GROUP_ID || null,
      class_ids: filter.CLASS_IDS || [],
      useCachedReport,
      isVarianceReport,
      regenerateCount,
    },
    {
      skip:
        Boolean(reportId) ||
        !groupId ||
        !entityId ||
        !reportType ||
        !filter.START_DATE ||
        !filter.END_DATE ||
        !filter.ACCOUNTING_METHOD,
    }
  );

  const { data: reportDataById, ...apiStatesById } =
    useGetInteractiveReportQuery(
      { groupId, entityId, reportId },
      { skip: !groupId || !entityId || !reportId }
    );

  const reportData = useMemo(() => {
    const sourceData = reportId ? reportDataById : data;
    return (sourceData as ReportByView)?.report_data || {};
  }, [reportId, reportDataById, data]);

  const formattedData = useMemo(() => {
    if (!reportData || Object.keys(reportData).length === 0) {
      return [];
    }
    return formatViewReportData(reportData);
  }, [reportData]);

  return {
    originalData: data || reportDataById,
    formatViewReportData: formattedData,
    ...(reportId ? apiStatesById : apiStates),
  };
};
